import { configuredCensusDimensions, normalizeRoom, normalizeUnitOrFloor } from "../../../../utils/censusResolution";
import type { DepartmentLocation } from "../../../../utils/models";
import { compareValues } from "../../../../utils/multiColumnSorting";

/**
 * The location popup's name and EMR rules. A location's census comes either from an EMR area (unit,
 * floor, rooms) or — with none of those — from the whole department (see `resolveLocationCensus`).
 * That second case is real and already how unmapped locations are staffed, so the popup makes the
 * admin choose it on purpose instead of landing in it by leaving the fields empty. Only the starting
 * choice depends on the department (see `initialDepartmentWideCensus`).
 */

/** The EMR area a location's census is read from, as the popup's fields hold it. */
export type EmrMapping = { unit: string | undefined; floor: string | undefined; rooms: string[] };

/**
 * A location name is its trimmed, case-sensitive text: the Locations page groups rows by it and the
 * backend's rename and EMR writes match on it, so "2 North" and "2 north" are two locations.
 */
const nameKey = (name: string): string => name.trim();

/** A blank unit or floor maps nothing — the same rule `configuredCensusDimensions` applies. */
const presentValue = (value: string | null | undefined): string | undefined => (value != null && value.trim() !== "" ? value : undefined);

export const isEmrMapped = (mapping: EmrMapping): boolean =>
	presentValue(mapping.unit) !== undefined || presentValue(mapping.floor) !== undefined || mapping.rooms.length > 0;

/** The unit, floor and room names the department's EMR lists offer; `null` for a list that hasn't loaded. */
export type EmrOptions = { units: readonly string[] | null; floors: readonly string[] | null; rooms: readonly string[] | null };

/**
 * The list's own spelling of `value`, matched the way census is (`censusDimensionKey`): a saved " 2N " or
 * a room "101A" is the list's "2N" / "101a", not a missing area. `undefined` when the list doesn't offer it.
 */
const offeredSpelling = (value: string, offered: readonly string[], normalize: (value: string) => string): string | undefined =>
	offered.find((option) => normalize(option) === normalize(value));

/**
 * The part of a mapping the EMR lists still offer, in the lists' spelling. A saved location can carry a
 * unit, floor or room the lists don't have (renamed in the EMR, or written before the lists existed): its
 * dropdown shows nothing and it matches no EMR area. Such a value isn't a census choice, so it neither
 * unblocks Save nor is saved again. A list that hasn't loaded keeps its values, since nothing can be told
 * about them yet.
 */
export const offeredEmrMapping = (mapping: EmrMapping, options: EmrOptions): EmrMapping => {
	const unit = presentValue(mapping.unit);
	const floor = presentValue(mapping.floor);
	const offeredRooms = options.rooms;
	return {
		unit: unit === undefined || options.units === null ? unit : offeredSpelling(unit, options.units, normalizeUnitOrFloor),
		floor: floor === undefined || options.floors === null ? floor : offeredSpelling(floor, options.floors, normalizeUnitOrFloor),
		rooms: offeredRooms === null ? mapping.rooms : [...new Set(mapping.rooms.flatMap((room) => offeredSpelling(room, offeredRooms, normalizeRoom) ?? []))],
	};
};

const emrMappingOf = (location: DepartmentLocation): EmrMapping => ({
	unit: presentValue(location.unit),
	floor: presentValue(location.floor),
	// DEFAULT: rooms come from a separate join and are absent on payloads that skip it — no rooms mapped.
	rooms: (location.rooms ?? []).map((room) => room.room_name),
});

const mappingSignature = (mapping: EmrMapping): string => JSON.stringify([mapping.unit ?? null, mapping.floor ?? null, [...mapping.rooms].sort()]);

/**
 * The names the name dropdown offers: each existing location name once, trimmed, plus the one in the
 * form so a just-added name stays selectable. Ordered like every other location list: the department's
 * custom location order when set (`customOrder`, null when off), then floor and unit numbers, then name.
 */
export const uniqueLocationNames = (locations: readonly DepartmentLocation[], currentName: string, customOrder: Map<string, number> | null): string[] => {
	const names = new Set([...locations.map((location) => location.location), currentName].map(nameKey));
	names.delete("");
	const order = customOrder === null ? null : byNameKey(customOrder);
	return [...names].sort((a, b) => compareValues(a, b, order));
};

/**
 * The custom order keyed by trimmed name, like the dropdown's options. The order saves names as they
 * were stored, so an older name with stray spaces would otherwise lose its place. When two spellings
 * trim to the same name, the earlier position wins.
 */
const byNameKey = (customOrder: Map<string, number>): Map<string, number> => {
	const order = new Map<string, number>();
	for (const [name, position] of customOrder) {
		const current = order.get(nameKey(name));
		if (current === undefined || position < current) order.set(nameKey(name), position);
	}
	return order;
};

/**
 * How other locations with this name are mapped to the EMR, for pre-filling the form when the admin
 * picks that name. Only mapped locations count, and never the one being edited. When they disagree the
 * mapping most of them share wins. A tie is broken by the mapping itself (unit, then floor, then rooms),
 * not by position: the department's locations arrive in no fixed order, so "the first one" could change
 * between two openings of the same popup. `null` when no same-named location is mapped.
 */
export const siblingEmrMapping = (locations: readonly DepartmentLocation[], name: string, excludeId: string | undefined): EmrMapping | null => {
	const tally = new Map<string, { mapping: EmrMapping; count: number }>();
	for (const location of locations) {
		if (location.id === excludeId || nameKey(location.location) !== nameKey(name)) continue;
		if (configuredCensusDimensions(location).length === 0) continue;
		const mapping = emrMappingOf(location);
		const signature = mappingSignature(mapping);
		const seen = tally.get(signature);
		if (seen) seen.count += 1;
		else tally.set(signature, { mapping, count: 1 });
	}
	let best: { signature: string; mapping: EmrMapping; count: number } | null = null;
	for (const [signature, entry] of tally) {
		if (best === null || entry.count > best.count || (entry.count === best.count && signature < best.signature)) best = { signature, ...entry };
	}
	return best === null ? null : best.mapping;
};

/** What picking a name in the dropdown changes: the name, and the EMR mapping to fill in when there is one. */
export type LocationNamePick = { name: string; mapping: EmrMapping | null };

/**
 * Picking or typing a name. `null` when it is the name the location already has, so re-picking it never
 * swaps the location's own mapping for another same-named location's.
 */
export const pickLocationName = (
	locations: readonly DepartmentLocation[],
	picked: string,
	currentName: string,
	excludeId: string | undefined,
): LocationNamePick | null => {
	const name = nameKey(picked);
	if (name === nameKey(currentName)) return null;
	return { name, mapping: siblingEmrMapping(locations, name, excludeId) };
};

/** The popup's census choice: Department wide census, or the EMR area in the fields. */
export type CensusChoice = { departmentWideCensus: boolean; mapping: EmrMapping };

/**
 * The census choice after the admin edits the unit, floor or rooms. Department wide and an area are
 * exclusive, so choosing an area unticks Department wide; emptying the fields never re-ticks it.
 */
export const censusChoiceAfterAreaChange = (current: CensusChoice, changes: Partial<EmrMapping>): CensusChoice => {
	const mapping = { ...current.mapping, ...changes };
	return { departmentWideCensus: current.departmentWideCensus && !isEmrMapped(mapping), mapping };
};

/** A name pick's fill-in that the admin hasn't touched yet: what it filled, and the choice it replaced. */
export type EmrAutofill = { filled: EmrMapping; replaced: CensusChoice };

/**
 * The census choice after picking a name. A name with a mapping fills it in; a name without one takes
 * back an earlier pick's fill-in, restoring the choice from before it (e.g. Department wide re-ticked),
 * so a mapping the admin never chose can't be saved under a name it doesn't belong to. Once the admin
 * edits the filled fields they are the admin's choice and a later pick keeps them.
 */
export const censusChoiceAfterNamePick = (
	current: CensusChoice,
	pickedMapping: EmrMapping | null,
	autofill: EmrAutofill | null,
): { choice: CensusChoice; autofill: EmrAutofill | null } => {
	const autofillUntouched = autofill !== null && !current.departmentWideCensus && mappingSignature(current.mapping) === mappingSignature(autofill.filled);
	const adminsChoice = autofillUntouched ? autofill.replaced : current;
	if (pickedMapping === null) return { choice: adminsChoice, autofill: null };
	return { choice: { departmentWideCensus: false, mapping: pickedMapping }, autofill: { filled: pickedMapping, replaced: adminsChoice } };
};

/**
 * Whether "Department wide census" starts ticked. A location with no EMR mapping is already staffed from
 * the department total, so a saved one opens ticked everywhere.
 * - PPD/HPPD: targets don't read the location's census, so the whole department is the everyday case —
 *   every unmapped location, new ones included, starts ticked and Save isn't held back by a choice
 *   admins rarely need to make.
 * - Ratio: the ladder runs against the location's own census, so a new location starts unticked and the
 *   admin picks an area or ticks it on purpose.
 */
export const initialDepartmentWideCensus = (existingConfig: DepartmentLocation | undefined, ratioDepartment: boolean): boolean => {
	const unmapped = existingConfig === undefined || configuredCensusDimensions(existingConfig).length === 0;
	// A config built from unmatched shifts carries no id at all (not even ""), so test truthiness.
	return unmapped && (!ratioDepartment || !!existingConfig?.id);
};

/** Where the PCC rooms list stands: until it has loaded, whether the department has rooms is unknown. */
export type RoomsListState = { state: "loading" | "failed" | "loaded"; count: number };

export type CensusScopeProblem = "missing" | "rooms_loading" | "rooms_failed";

/**
 * Why Save is blocked on the census choice, or `null`. A department with EMR areas needs an area or
 * Department wide census. When rooms are the only areas there could be, Save also waits for the rooms
 * list: until it loads the popup can't tell whether to ask, and an unmapped save would silently read the
 * whole department's census.
 */
export const censusScopeProblem = ({
	unitsOrFloorsAvailable,
	rooms,
	mapping,
	departmentWideCensus,
}: {
	unitsOrFloorsAvailable: boolean;
	rooms: RoomsListState;
	mapping: EmrMapping;
	departmentWideCensus: boolean;
}): CensusScopeProblem | null => {
	if (departmentWideCensus || isEmrMapped(mapping)) return null;
	if (!unitsOrFloorsAvailable && rooms.state === "loading") return "rooms_loading";
	if (!unitsOrFloorsAvailable && rooms.state === "failed") return "rooms_failed";
	return unitsOrFloorsAvailable || rooms.count > 0 ? "missing" : null;
};
